import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const INK = "#1A1A2E";
const MUTED = "#5b5e69";
const GREEN = "#0F7A3E";
const RED = "#B4241E";

const money = (cents) =>
  cents ? `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—";

const timeAgo = (iso) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  const h = Math.floor(diff / (1000 * 60 * 60));
  if (h < 1) return "just now";
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

/**
 * Public Booked-Job Ledger page (BR-2 · PM32).
 * URL: /ledger/:token — token is contractor-scoped, no login required.
 */
export default function LedgerPublicPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, err: "" });
  const [pendingId, setPendingId] = useState(null);
  const [dollarInputs, setDollarInputs] = useState({});
  const [flash, setFlash] = useState(null);

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await axios.get(`${API}/ledger/public/${token}`);
      setState({ loading: false, data: r.data, err: "" });
    } catch (e) {
      setState({ loading: false, data: null, err: e?.response?.data?.detail || e.message });
    }
  };
  useEffect(() => { load(); }, [token]); // eslint-disable-line

  const decide = async (leadId, decision) => {
    setPendingId(leadId + ":" + decision);
    setFlash(null);
    const dollars = decision === "won" ? Number(dollarInputs[leadId] || 0) : null;
    if (decision === "won" && !dollars) {
      setFlash({ type: "err", msg: "Enter the booked dollar amount before tapping WON." });
      setPendingId(null);
      return;
    }
    try {
      await axios.post(`${API}/ledger/public/${token}/confirm`, {
        lead_id: leadId, decision, dollars,
      });
      setFlash({ type: "ok", msg: decision === "won" ? `Booked ${money(dollars * 100)} — nice.` :
                                    decision === "lost" ? "Marked lost." : "Snoozed for later." });
      await load();
    } catch (e) {
      setFlash({ type: "err", msg: e?.response?.data?.detail || e.message });
    } finally {
      setPendingId(null);
    }
  };

  if (state.loading) return <Shell><p style={{ color: MUTED }}>Loading your ledger…</p></Shell>;
  if (state.err || !state.data?.ok) {
    return (
      <Shell>
        <div data-testid="ledger-invalid" style={{ padding: 20, background: "#FDECEA", border: `1px solid ${RED}`, color: RED, borderRadius: 8 }}>
          This ledger link is invalid or expired. Text us at contact@nearnow.biz to get a fresh one.
        </div>
      </Shell>
    );
  }

  const { contractor, unconfirmed = [], confirmed_recent = [], week_booked_cents = 0 } = state.data;

  return (
    <Shell>
      <div data-testid="ledger-public-page">
        <p style={{ margin: 0, color: GOLD, fontSize: 13, letterSpacing: 3, fontFamily: "'Bebas Neue', sans-serif" }}>
          NEARNOW · YOUR BOOKED-JOB LEDGER
        </p>
        <h1 className="font-display" style={{ color: NAVY, fontSize: 42, margin: "6px 0 0", lineHeight: 1 }}>
          {contractor?.business_name || "Your book"}
        </h1>
        <p style={{ color: MUTED, marginTop: 6, fontSize: 16, maxWidth: 640 }}>
          Every call ARIA picked up for you. Tap <strong style={{ color: GREEN }}>WON</strong>, <strong style={{ color: RED }}>LOST</strong>, or <em>Later</em>. Wins with a dollar amount count toward your Weekly Receipt.
        </p>

        <div
          data-testid="ledger-week-strip"
          style={{
            background: NAVY, color: "#fff", padding: "16px 20px",
            borderRadius: 12, marginTop: 18, border: `1.5px solid ${GOLD}`,
            display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
          }}
        >
          <div>
            <div style={{ fontSize: 12, color: GOLD, letterSpacing: 2, fontFamily: "'Bebas Neue', sans-serif" }}>
              BOOKED (LAST 7 DAYS)
            </div>
            <div style={{ fontSize: 34, fontWeight: 800 }}>{money(week_booked_cents)}</div>
          </div>
          <div style={{ fontSize: 13, color: "#cbd0db" }}>
            {confirmed_recent.length} confirmed win{confirmed_recent.length === 1 ? "" : "s"}
          </div>
        </div>

        {flash && (
          <div
            data-testid="ledger-flash"
            style={{
              marginTop: 12, padding: "10px 14px", borderRadius: 8, fontSize: 14,
              background: flash.type === "ok" ? "#E7F4EC" : "#FDECEA",
              color: flash.type === "ok" ? GREEN : RED,
              border: `1px solid ${flash.type === "ok" ? GREEN : RED}`,
            }}
          >
            {flash.msg}
          </div>
        )}

        <h2 className="font-display" style={{ marginTop: 26, color: NAVY, fontSize: 24, letterSpacing: 1 }}>
          UNCONFIRMED · {unconfirmed.length}
        </h2>
        {unconfirmed.length === 0 && (
          <p style={{ color: MUTED, marginTop: 6 }} data-testid="ledger-empty">
            Nothing waiting on you. When ARIA picks up your next call, it lands here in 24 hours.
          </p>
        )}
        <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
          {unconfirmed.map((row) => (
            <div key={row.id} data-testid={`ledger-row-${row.id}`} style={rowStyle}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 17, color: INK }}>
                  {row.caller_name || "Caller"}
                </div>
                {row.urgency === "urgent" && (
                  <span style={urgentBadge}>URGENT</span>
                )}
                <div style={{ fontSize: 12, color: MUTED, marginLeft: "auto" }}>
                  {timeAgo(row.created_at)}
                </div>
              </div>
              <div style={{ color: MUTED, fontSize: 14, marginTop: 4 }}>
                {row.trade_needed} · <a href={`tel:${row.caller_phone}`} style={{ color: NAVY }}>{row.caller_phone}</a>
              </div>
              {row.job_description && (
                <div style={{ marginTop: 8, fontSize: 14, color: INK, lineHeight: 1.4 }}>
                  {row.job_description}
                </div>
              )}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12, alignItems: "center" }}>
                <div style={{ position: "relative" }}>
                  <span style={{ position: "absolute", left: 10, top: 12, color: MUTED }}>$</span>
                  <input
                    data-testid={`ledger-amount-${row.id}`}
                    type="number" min="0" step="1"
                    placeholder="dollars"
                    value={dollarInputs[row.id] || ""}
                    onChange={(e) => setDollarInputs({ ...dollarInputs, [row.id]: e.target.value })}
                    style={dollarInput}
                    inputMode="numeric"
                  />
                </div>
                <button
                  data-testid={`ledger-won-${row.id}`}
                  onClick={() => decide(row.id, "won")}
                  disabled={pendingId?.startsWith(row.id)}
                  style={{ ...actionBtn, background: GREEN, color: "#fff" }}
                >
                  {pendingId === `${row.id}:won` ? "…" : "WON"}
                </button>
                <button
                  data-testid={`ledger-lost-${row.id}`}
                  onClick={() => decide(row.id, "lost")}
                  disabled={pendingId?.startsWith(row.id)}
                  style={{ ...actionBtn, background: "#fff", color: RED, border: `1.5px solid ${RED}` }}
                >
                  LOST
                </button>
                <button
                  data-testid={`ledger-later-${row.id}`}
                  onClick={() => decide(row.id, "later")}
                  disabled={pendingId?.startsWith(row.id)}
                  style={{ ...actionBtn, background: "transparent", color: MUTED }}
                >
                  Later
                </button>
              </div>
            </div>
          ))}
        </div>

        {confirmed_recent.length > 0 && (
          <>
            <h2 className="font-display" style={{ marginTop: 30, color: NAVY, fontSize: 24, letterSpacing: 1 }}>
              CONFIRMED (LAST 7 DAYS)
            </h2>
            <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
              {confirmed_recent.map((row) => (
                <div key={row.id} data-testid={`ledger-confirmed-${row.id}`} style={{ ...rowStyle, borderColor: "#d7ecd7" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
                    <div>
                      <div style={{ fontWeight: 700, color: INK }}>{row.caller_name}</div>
                      <div style={{ fontSize: 12, color: MUTED }}>{row.trade_needed}</div>
                    </div>
                    <div style={{ fontWeight: 800, color: GREEN, fontSize: 20 }}>
                      {money(row.job_value_cents)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <p style={{ color: MUTED, fontSize: 12, marginTop: 26 }}>
          Bookmark this page. New calls show up automatically. Questions? Text us at contact@nearnow.biz.
        </p>
      </div>
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div style={{ minHeight: "100vh", background: "#F5F6FA", padding: "24px 16px 60px" }}>
    <div style={{ maxWidth: 720, margin: "0 auto", background: "#fff", padding: 24, borderRadius: 14, boxShadow: "0 8px 24px rgba(10,22,40,0.08)" }}>
      {children}
    </div>
  </div>
);

const rowStyle = {
  background: "#fff",
  border: "1px solid #e5e7ee",
  borderRadius: 10,
  padding: "14px 16px",
};
const dollarInput = {
  width: 110, padding: "10px 10px 10px 22px", fontSize: 15,
  border: `1.5px solid rgba(10,22,40,0.15)`, borderRadius: 6,
  minHeight: 44,
};
const actionBtn = {
  padding: "10px 16px", borderRadius: 6, border: "none",
  fontWeight: 700, letterSpacing: 1, fontSize: 14, minHeight: 44,
  cursor: "pointer", fontFamily: "'Bebas Neue', sans-serif",
};
const urgentBadge = {
  background: RED, color: "#fff", padding: "2px 8px",
  borderRadius: 4, fontSize: 10, letterSpacing: 2, fontFamily: "'Bebas Neue', sans-serif",
};
